/**
 * API Utility with Club Authentication & JWT
 * 
 * All API calls should use this utility to ensure proper authentication.
 * Includes both X-Club-Key (client identification) and Bearer token (user auth).
 */

// Storage keys
const CLUB_KEY_STORAGE = 'masaklubi_club_key';
const USER_STORAGE = 'masaklubi_user';
const ACCESS_TOKEN_STORAGE = 'masaklubi_access_token';

/**
 * Get the stored club API key
 */
export function getClubKey() {
    return localStorage.getItem(CLUB_KEY_STORAGE);
}

/**
 * Get the stored JWT access token
 */
export function getAccessToken() {
    return localStorage.getItem(ACCESS_TOKEN_STORAGE);
}

/**
 * Set the club API key (called after successful login)
 */
export function setClubKey(apiKey) {
    if (apiKey) {
        localStorage.setItem(CLUB_KEY_STORAGE, apiKey);
    }
}

/**
 * Set the JWT access token (called after successful login)
 */
export function setAccessToken(token) {
    if (token) {
        localStorage.setItem(ACCESS_TOKEN_STORAGE, token);
    }
}

/**
 * Clear the club API key (called on logout)
 */
export function clearClubKey() {
    localStorage.removeItem(CLUB_KEY_STORAGE);
}

/**
 * Clear the access token (called on logout)
 */
export function clearAccessToken() {
    localStorage.removeItem(ACCESS_TOKEN_STORAGE);
}

/**
 * Get stored user data
 */
export function getStoredUser() {
    const saved = localStorage.getItem(USER_STORAGE);
    return saved ? JSON.parse(saved) : null;
}

/**
 * Store user data and authentication tokens
 */
export function storeUser(user) {
    // Store user data (without tokens for security)
    const userToStore = { ...user };
    delete userToStore.accessToken; // Don't store JWT in user object
    localStorage.setItem(USER_STORAGE, JSON.stringify(userToStore));
    
    // Store club API key separately for easy access
    if (user?.club?.apiKey) {
        setClubKey(user.club.apiKey);
    }
    
    // Store JWT access token separately
    if (user?.accessToken) {
        setAccessToken(user.accessToken);
    }
}

/**
 * Clear all auth data (including SuperAdmin verification)
 */
export function clearAuth() {
    localStorage.removeItem(USER_STORAGE);
    clearClubKey();
    clearAccessToken();
    // Also clear SuperAdmin verification when logging out
    localStorage.removeItem('masaklubi_superadmin_verified');
}

/**
 * Decode JWT payload without verifying signature (client-side expiry check only)
 */
function decodeJwtPayload(token) {
    try {
        const parts = token.split('.');
        if (parts.length < 2) return null;
        const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
        const json = decodeURIComponent(
            atob(base64)
                .split('')
                .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
                .join('')
        );
        return JSON.parse(json);
    } catch {
        return null;
    }
}

/**
 * True if access token exists and is not expired (small clock skew allowed)
 */
export function isAccessTokenValid(skewMs = 30_000) {
    const token = getAccessToken();
    if (!token) return false;
    const payload = decodeJwtPayload(token);
    if (!payload) return false;
    if (typeof payload.exp !== 'number') return false;
    return payload.exp * 1000 > Date.now() + skewMs;
}

/**
 * Check if user is authenticated (valid JWT + club key)
 */
export function isAuthenticated() {
    return !!getClubKey() && isAccessTokenValid();
}

export const AUTH_EXPIRED_EVENT = 'masaklubi:auth-expired';

function notifyAuthExpired() {
    try {
        window.dispatchEvent(new CustomEvent(AUTH_EXPIRED_EVENT));
    } catch (e) {
        // ignore (non-browser)
    }
}

/**
 * Make an authenticated API request
 * Automatically includes X-Club-Key and Authorization Bearer headers
 * 
 * @param {string} url - API endpoint (e.g., '/api/games')
 * @param {object} options - Fetch options (method, body, etc.)
 * @returns {Promise<Response>} - Fetch response
 */
export async function apiFetch(url, options = {}) {
    const clubKey = getClubKey();
    const accessToken = getAccessToken();
    
    // Build headers
    const headers = {
        'Content-Type': 'application/json',
        ...options.headers
    };
    
    // Public endpoints that don't require authentication
    const publicEndpoints = [
        '/api/login', 
        '/api/onboarding', 
        '/api/public-config',
        '/api/bgg/search', 
        '/api/bgg/hot',
        '/api/superadmin/auth'
    ];
    const isPublic = publicEndpoints.some(ep => url.startsWith(ep));
    
    // Add authentication headers for non-public endpoints
    if (!isPublic) {
        if (clubKey) {
            headers['X-Club-Key'] = clubKey;
        }
        if (accessToken) {
            headers['Authorization'] = `Bearer ${accessToken}`;
        }
    }
    
    // Make request
    const response = await fetch(url, {
        ...options,
        headers
    });
    
    // Expired/invalid JWT → clear session so UI returns to landing/login
    if (response.status === 401 && !isPublic && accessToken) {
        console.warn('[API] 401 Unauthorized - clearing expired session');
        clearAuth();
        notifyAuthExpired();
    }
    
    // Handle 404 which might mean invalid/missing club key
    if (response.status === 404) {
        const text = await response.text();
        try {
            const json = JSON.parse(text);
            if (json.error === 'Not found' && !isPublic) {
                console.warn('[API] 404 response - might be auth issue');
            }
        } catch (e) {
            // Not JSON, just a regular 404
        }
        // Return a new response since we consumed the body
        return new Response(text, {
            status: response.status,
            statusText: response.statusText,
            headers: response.headers
        });
    }
    
    return response;
}

/**
 * GET request helper
 */
export async function apiGet(url) {
    return apiFetch(url, { method: 'GET' });
}

/**
 * POST request helper
 */
export async function apiPost(url, data) {
    return apiFetch(url, {
        method: 'POST',
        body: JSON.stringify(data)
    });
}

/**
 * PUT request helper
 */
export async function apiPut(url, data) {
    return apiFetch(url, {
        method: 'PUT',
        body: JSON.stringify(data)
    });
}

/**
 * DELETE request helper
 */
export async function apiDelete(url, data = null) {
    const options = { method: 'DELETE' };
    if (data) {
        options.body = JSON.stringify(data);
    }
    return apiFetch(url, options);
}

/**
 * Upload file with authentication
 */
export async function apiUpload(url, formData) {
    const clubKey = getClubKey();
    const accessToken = getAccessToken();
    
    const headers = {};
    if (clubKey) {
        headers['X-Club-Key'] = clubKey;
    }
    if (accessToken) {
        headers['Authorization'] = `Bearer ${accessToken}`;
    }
    
    // Don't set Content-Type for FormData - browser will set it with boundary
    return fetch(url, {
        method: 'POST',
        headers,
        body: formData
    });
}

// Default export for convenience
export default {
    fetch: apiFetch,
    get: apiGet,
    post: apiPost,
    put: apiPut,
    delete: apiDelete,
    upload: apiUpload,
    getClubKey,
    setClubKey,
    clearClubKey,
    getAccessToken,
    setAccessToken,
    clearAccessToken,
    getStoredUser,
    storeUser,
    clearAuth,
    isAuthenticated,
    isAccessTokenValid,
    AUTH_EXPIRED_EVENT
};
